import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Award } from 'lucide-react';

export default function EducationSection({ educationInfo }) {
  if (!educationInfo.display || !educationInfo.schools.length) return null;

  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariant = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 100 },
    },
  };

  return (
    <section id="education" className="py-20 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 flex items-center justify-center gap-3">
            <BookOpen size={40} className="text-primary-500" />
            Education
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400">
            My academic background and qualifications
          </p>
        </motion.div>

        {/* Education Cards */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 gap-8"
        >
          {educationInfo.schools.map((school, i) => (
            <motion.div
              key={i}
              variants={itemVariant}
              className="group relative h-full"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-secondary-500/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              <div className="relative bg-white dark:bg-slate-800 rounded-xl p-8 border border-gray-200 dark:border-slate-700 h-full flex flex-col hover:shadow-xl transition-shadow duration-300">
                {/* Logo */}
                {school.logo && (
                  <motion.img
                    whileHover={{ scale: 1.1 }}
                    src={school.logo}
                    alt={school.schoolName}
                    className="w-16 h-16 rounded-lg mb-4 object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}

                {/* Content */}
                <h3 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">
                  {school.schoolName}
                </h3>
                <p className="text-lg font-semibold text-primary-600 dark:text-primary-400 mb-2">
                  {school.subHeader}
                </p>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {school.duration}
                </p>

                {/* GPA or description */}
                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300 font-semibold">
                  <Award size={20} className="text-yellow-500" />
                  {school.desc}
                </div>

                {/* Decoration */}
                <div className="mt-auto pt-4 border-t border-gray-200 dark:border-slate-700">
                  <div className="w-full h-1 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full" />
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
